import attributeCRUDActions from '@mm/state/actions/AttributeCRUDActions';
import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import ChatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import pageCRUDActions from '@mm/state/actions/PageCRUDActions';
import pageGroupCRUDActions from '@mm/state/actions/PageGroupCRUDActions';
import sessionCRUDActions from '@mm/state/actions/SessionCRUDActions';
import tagsCRUDActions from '@mm/state/actions/TagsCRUDActions';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import viewActions from '@mm/state/actions/viewActions';
import { UserAttributes } from '@mm/state/model/CompanyUsersModel';
import { PageKind } from '@mm/state/model/PageModel';
import modalActions, {
  OpenPrettyErrorModalParams,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
  selectBasicPages,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectInviteToken } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { RouterComponentProps } from '../../../common/helpers/routes/interfaces';
import {
  ensureSequentialOrder,
  isOrderBroken,
} from '../../../common/helpers/utils/sorting';
import {
  InjectedSafeTimeout,
  withSafeTimeout,
} from '../../../common/hocs/withSafeTimeout';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal/ConfirmDeleteModal';
import DeleteErrorModal from '../../components/DeleteErrorModal/DeleteErrorModal';
import { ChatbotEditor } from './ChatbotEditor';
import './ChatbotEditorPage.pcss';
import { CONFIRM_PAGE_DELETE_TEXT } from './constants';
import FetchBotData from './FetchBotData/FetchBotData';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps &
  InjectedSafeTimeout;

export type ChatbotEditorPageProps = OuterProps & InnerProps;

const mapStateToProps = (state: StoreState) => {
  const activeCompany = activeCompanySelector(state);

  let isCompAdmin = false;
  if (activeCompany && activeCompany.editable) {
    isCompAdmin = true;
  }
  return {
    companyId: selectActiveCompanyId(state),
    confirmDeletePageGroup: state.view.chatbotEditor.confirmDeletePageGroup,
    confirmDeletePage: state.view.chatbotEditor.confirmDeletePage,
    isSysAdmin: state.session.session.admin,
    mmBuyBeforeTrying: state.session.session.userAttributes?.mmBuyBeforeTrying,
    pages: state.page.entities,
    landingPages: state.landingPages.entities,
    activeChatbotId: activeChatbotIdSelector(state),
    freeOrSpecialPricePlan:
      activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
    isCompAdmin,
    activeChatbot: state.chatbot.activeChatbot,
    basicPagesList: selectBasicPages(state),
    inviteToken: selectInviteToken(state),
    session: state.session.session,
    activeCompany,
    availablePages: state.connectedPage.availablePages,
    modalOpen: state.modalState.isOpen || state.modalState.isTourOpen,
    activeMessagesStatistic: activeMessagesStatisticSelector(state),
    activeFacebookPage: activeFacebookPageSelector(state),
    justCompletedOnboarding: state.onboarding.justCompletedOnboarding,
    audienceExtractorChannel: state.onboarding.audienceExtractorChannel,
    featureFlags: combinedFeatureFlagsSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getChatbotList: (companyId: number) =>
    dispatch(ChatbotCRUDActions.retrieveBasicListByCompany(companyId)),
  getPageGroups: (chatbotId: number) =>
    dispatch(pageGroupCRUDActions.retrieveAll(chatbotId)),
  getDropDownPages: (chatbotId: number) =>
    dispatch(pageCRUDActions.retriveDropdownPages(chatbotId)),
  getPages: (chatbotId: number) =>
    dispatch(pageCRUDActions.retrieveAll(chatbotId)),
  activateChatbotEditor: (chatbotId: number, userId: number) =>
    dispatch(viewActions.activateChatbotEditor(chatbotId, userId)),
  getAttributes: (pageId: number) =>
    dispatch(attributeCRUDActions.retrieveAll(pageId)),
  retrieveBusinessAttributes: (pageId: number) =>
    dispatch(attributeCRUDActions.retrieveBusinessAttributes(pageId)),
  getAudiences: (chatbotId: number) =>
    dispatch(audiencesCRUDActions.retrieveAll(chatbotId)),
  onDeletePageGroupClose: () =>
    dispatch(viewActions.hideConfirmDeletePageGroup()),
  deletePageGroup: (chatbotId: number, pageGroupId: number) =>
    dispatch(pageGroupCRUDActions.delete(chatbotId, pageGroupId)),
  onDeletePageClose: () => dispatch(viewActions.hideConfirmDeletePage()),
  deletePage: (chatbotId: number, pageId: number) =>
    dispatch(pageCRUDActions.delete(chatbotId, pageId)),
  updatePages: entities => dispatch(pageCRUDActions.batchUpdate(entities)),
  deactivatePageEditor: () => dispatch(viewActions.deactivatePageEditor()),
  retrieveChatbot: (
    chatbotId: number,
    noActiveBotChange?: boolean,
    throwExceptions?: boolean,
  ) =>
    dispatch(
      ChatbotCRUDActions.retrieve(
        chatbotId,
        noActiveBotChange,
        throwExceptions,
      ),
    ),
  getUsers: (companyId: number, page?: number, query?: string) =>
    dispatch(usersCRUDActions.retrieveAll(companyId, page, 100, query)),
  retrieveFacebookPage: (facebookPageId: number) =>
    dispatch(connectedPageCRUDActions.retrievePage(facebookPageId)),
  resetToken: () => dispatch(sessionCRUDActions.resetInviteToken()),
  activateCompany: (entityId: number, id: number) =>
    dispatch(usersCRUDActions.updateActiveCompanyId(entityId, id)),
  updateUserAttributes: (
    companyId: number,
    entityId: number,
    updatedStatuses: Partial<UserAttributes>,
  ) =>
    dispatch(
      usersCRUDActions.updateUserAttributes(
        companyId,
        entityId,
        updatedStatuses,
      ),
    ),
  getFacebookPageList: (companyId: number) =>
    dispatch(connectedPageCRUDActions.retrieveBasicListByCompany(companyId)),
  getTags: (facebookPageId: number) =>
    dispatch(tagsCRUDActions.getAll(facebookPageId, false, ['otn', 'regular'])),
  openMonthlySendsWarningModal: () =>
    dispatch(modalActions.openMonthlySendsWarningModal()),
  openPrettyErrorModal: (args: OpenPrettyErrorModalParams) =>
    dispatch(modalActions.openPrettyErrorModal(args)),
});

class ChatbotEditorPage extends Component<ChatbotEditorPageProps, {}> {
  onDeletePage = async (chatbotId: number, pageId: number) => {
    const {
      history,
      pages,
      activeChatbotId,
      confirmDeletePage: { groupPages },
      deletePage,
      updatePages,
      deactivatePageEditor,
    } = this.props;
    const pagesAfterDelete = groupPages.filter(p => p.id !== pageId);
    await deletePage(chatbotId, pageId);
    if (isOrderBroken(pagesAfterDelete)) {
      const correctOrderPages = ensureSequentialOrder(pagesAfterDelete);
      const fixedPagesPatch = correctOrderPages.map((p, i) => ({
        id: p.id,
        sortOrder: i,
      }));
      updatePages(fixedPagesPatch);
    }
    deactivatePageEditor();
    const welcomePage = pages.find(page => page.kind === PageKind.WelcomePage);
    const dialogueId = welcomePage && welcomePage.id;
    history.push(
      BotBuilderRoutes.build.getLink({
        chatbotId: activeChatbotId,
        dialogueId,
      }),
    );
  };

  isLandingPageConnectedToPage(pageId: number) {
    const { landingPages } = this.props;
    if (landingPages && landingPages.length > 0) {
      if (landingPages.some(lp => lp.pageId === pageId)) return true;
    }
    return false;
  }

  render() {
    const {
      confirmDeletePageGroup,
      confirmDeletePage,
      onDeletePageGroupClose,
      deletePageGroup,
      onDeletePageClose,
      companyId,
    } = this.props;

    if (!companyId) return null;

    return (
      <FetchBotData chatbotEditorPageProps={this.props}>
        <>
          <ConfirmDeleteModal
            headerText={
              <span>
                Delete the folder <b>{confirmDeletePageGroup.groupName}</b> and
                all contained dialogues?
              </span>
            }
            show={confirmDeletePageGroup.show}
            onClose={onDeletePageGroupClose}
            onDelete={deletePageGroup}
            selectedEntityId={confirmDeletePageGroup.pageGroupId}
          />
          <ConfirmDeleteModal
            headerText={CONFIRM_PAGE_DELETE_TEXT}
            show={confirmDeletePage.show}
            onClose={onDeletePageClose}
            onDelete={this.onDeletePage}
            selectedEntityId={confirmDeletePage.pageId}
          >
            {confirmDeletePage.show &&
              this.isLandingPageConnectedToPage(confirmDeletePage.pageId) && (
                <div>
                  <p>
                    You have lead magnet(s), e.g. a Landing Page or a Scan Code
                    connected to this page.
                  </p>
                  <p className="chatbot-editor-page__danger-text">
                    Deleting this page will also delete those Lead Magnet(s)!
                  </p>
                </div>
              )}
          </ConfirmDeleteModal>
          <DeleteErrorModal />
          <ChatbotEditor />
        </>
      </FetchBotData>
    );
  }
}

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withSafeTimeout,
);

export default enhance(ChatbotEditorPage);
